/**
 * Pure caseload filter / sort / group helpers (issue #436).
 *
 * The CaseloadToolbar keeps the state, but the actual transformation
 * over `StudentSummary[]` lives here so:
 *
 *   1. The screen stays thin (no business logic in render).
 *   2. Unit tests under vitest can pin the behaviour without rendering
 *      React or stubbing Supabase.
 *   3. We can reuse the same grouping for hypothetical admin views.
 *
 * Pure functions only — no IO, no React, no Supabase imports.
 */
import type { StudentSummary } from '../effectiveslp/students';

/**
 * Filter / sort axes. Kept as a single object so the toolbar's
 * draft state is a plain serialisable snapshot that flows into
 * sessionStorage (acceptance criterion #7).
 */
export interface CaseloadQuery {
  search: string;
  goalArea: GoalAreaFilter;
  needsNewSession: boolean;
  activeGoals: ActiveGoalsFilter;
  sort: SortKey;
}

export type GoalAreaFilter = 'all' | string;
export type ActiveGoalsFilter = 'all' | 'with' | 'without';

/**
 * Same SortKey union that CaseloadToolbar exports. Duplicated here
 * to avoid a circular import — the screen consumes both.
 */
export type SortKey = 'last_session' | 'name' | 'created' | 'active_goals';

/**
 * Section header for the group-by-last-session render. The bucketing
 * is local-tz aware (Date.getTime uses local clock; offset is the
 * caller's responsibility via the epoch-ms constructor in tests).
 */
export type SessionBucket =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'older'
  | 'never';

export const SESSION_BUCKET_LABELS: Record<SessionBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  this_month: 'This month',
  older: 'Older',
  never: 'Never',
};

export const SESSION_BUCKET_ORDER: SessionBucket[] = [
  'today',
  'yesterday',
  'this_week',
  'this_month',
  'older',
  'never',
];

/**
 * Day window (in ms) for bucketing last_session_at. "This week"
 * is anything within the last 7 days but not today / yesterday;
 * "this month" is anything within the last 30 days but not in the
 * "this week" bucket.
 */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Bucket a last-session timestamp into one of the six render
 * sections. A null timestamp is `never`. Future timestamps (clock
 * skew, demo data) collapse into `today` rather than risk an
 * off-by-one.
 */
export function bucketForLastSession(
  lastSession: string | null,
  now: number = Date.now(),
): SessionBucket {
  if (lastSession === null) return 'never';
  const ts = Date.parse(lastSession);
  if (Number.isNaN(ts)) return 'never';
  const delta = now - ts;
  if (delta < 0) return 'today';
  if (delta < ONE_DAY_MS) return 'today';
  if (delta < 2 * ONE_DAY_MS) return 'yesterday';
  if (delta < 7 * ONE_DAY_MS) return 'this_week';
  if (delta < 30 * ONE_DAY_MS) return 'this_month';
  return 'older';
}

/**
 * 14-day threshold matches the issue's acceptance criterion:
 * "when checked, only show students with last_session_at older
 * than 14 days OR null".
 */
const NEEDS_NEW_SESSION_DAYS = 14;
const NEEDS_NEW_SESSION_MS = NEEDS_NEW_SESSION_DAYS * ONE_DAY_MS;

/**
 * Apply the full filter pipeline (search, goal area, needs-new-session,
 * active goals). Order does not matter — the predicate is monotone
 * AND across all four axes.
 */
export function filterCaseload(
  students: ReadonlyArray<StudentSummary>,
  query: CaseloadQuery,
  now: number = Date.now(),
): StudentSummary[] {
  const term = query.search.trim().toLowerCase();
  const goalArea = query.goalArea;
  const needsNewSession = query.needsNewSession;
  const activeGoals = query.activeGoals;

  return students.filter((s) => {
    if (term.length > 0) {
      const name = s.firstName.toLowerCase();
      if (!name.includes(term)) return false;
    }
    if (goalArea !== 'all') {
      // #485: the chip set is `GOAL_AREAS` (Voice, Cognitive, …)
      // which is a per-goal concept, not a per-student diagnosis
      // category. The previous implementation filtered on
      // `disorderCategory` which silently broke the Voice /
      // Cognitive chips (those areas are not in the disorder
      // category enum at all) and mis-labelled the rest. The
      // `goalAreas` rollup is the server-rolled DISTINCT `area`
      // values across the student's active goals
      // (supabase/migrations/20260821000001_list_students_with_counts_goal_areas.sql).
      if (!s.goalAreas.includes(goalArea)) return false;
    }
    if (needsNewSession) {
      if (s.lastSessionDate === null) {
        // fall through — null counts as "needs a session".
      } else {
        const ts = Date.parse(s.lastSessionDate);
        if (!Number.isNaN(ts) && now - ts < NEEDS_NEW_SESSION_MS) {
          return false;
        }
      }
    }
    if (activeGoals !== 'all') {
      const hasActive = s.activeGoalsCount > 0;
      if (activeGoals === 'with' && !hasActive) return false;
      if (activeGoals === 'without' && hasActive) return false;
    }
    return true;
  });
}

/**
 * Apply the sort axis. Mirrors the previous local sort logic so
 * existing behaviour (null-last for last_session) is preserved.
 */
export function sortCaseload(
  students: ReadonlyArray<StudentSummary>,
  sort: SortKey,
): StudentSummary[] {
  const rows = students.slice();
  rows.sort((a, b) => {
    switch (sort) {
      case 'name': {
        return a.firstName.localeCompare(b.firstName);
      }
      case 'created': {
        return b.createdAt.localeCompare(a.createdAt);
      }
      case 'active_goals': {
        // Descending — students with more goals first.
        if (a.activeGoalsCount !== b.activeGoalsCount) {
          return b.activeGoalsCount - a.activeGoalsCount;
        }
        return a.firstName.localeCompare(b.firstName);
      }
      case 'last_session':
      default: {
        if (a.lastSessionDate === null && b.lastSessionDate === null) {
          return b.createdAt.localeCompare(a.createdAt);
        }
        if (a.lastSessionDate === null) return 1;
        if (b.lastSessionDate === null) return -1;
        return b.lastSessionDate.localeCompare(a.lastSessionDate);
      }
    }
  });
  return rows;
}

export interface GroupedSessionSection {
  bucket: SessionBucket;
  label: string;
  students: StudentSummary[];
}

/**
 * Group a (already last-session-sorted) list into render sections.
 * Preserves within-bucket ordering from the caller. Sections with
 * zero rows are omitted from the result.
 */
export function groupByLastSession(
  students: ReadonlyArray<StudentSummary>,
  now: number = Date.now(),
): GroupedSessionSection[] {
  const buckets = new Map<SessionBucket, StudentSummary[]>();
  for (const s of students) {
    const bucket = bucketForLastSession(s.lastSessionDate, now);
    const list = buckets.get(bucket);
    if (list) list.push(s);
    else buckets.set(bucket, [s]);
  }
  const sections: GroupedSessionSection[] = [];
  for (const bucket of SESSION_BUCKET_ORDER) {
    const list = buckets.get(bucket);
    if (!list || list.length === 0) continue;
    sections.push({
      bucket,
      label: SESSION_BUCKET_LABELS[bucket],
      students: list,
    });
  }
  return sections;
}

/**
 * sessionStorage key. Versioned so a future schema bump can shift
 * the shape without crashing old tabs.
 */
export const CASELOAD_QUERY_STORAGE_KEY = 'effectiveslp:caseloadQuery:v1';

export function readQueryFromStorage(): Partial<CaseloadQuery> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(CASELOAD_QUERY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Partial<CaseloadQuery>;
  } catch {
    return null;
  }
}

export function writeQueryToStorage(query: CaseloadQuery): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      CASELOAD_QUERY_STORAGE_KEY,
      JSON.stringify(query),
    );
  } catch {
    // Quota / private-mode failures are non-fatal.
  }
}

/**
 * Sanitize a partial read from sessionStorage so a stale or
 * hand-edited value can't crash the toolbar. Anything not
 * recognised is dropped back to the default.
 */
export function sanitizeQuery(input: unknown): CaseloadQuery | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  const defaultSort: SortKey = 'last_session';
  const sortCandidates: SortKey[] = [
    'last_session',
    'name',
    'created',
    'active_goals',
  ];
  const rawSort = typeof o.sort === 'string' ? o.sort : defaultSort;
  const sort = (sortCandidates as string[]).includes(rawSort)
    ? (rawSort as SortKey)
    : defaultSort;

  const search = typeof o.search === 'string' ? o.search : '';
  const goalArea = typeof o.goalArea === 'string' ? o.goalArea : 'all';

  const activeGoalsCandidates: ActiveGoalsFilter[] = [
    'all',
    'with',
    'without',
  ];
  const rawActive = typeof o.activeGoals === 'string' ? o.activeGoals : 'all';
  const activeGoals = (activeGoalsCandidates as string[]).includes(rawActive)
    ? (rawActive as ActiveGoalsFilter)
    : 'all';

  const needsNewSession = o.needsNewSession === true;

  return { search, goalArea, needsNewSession, activeGoals, sort };
}
